/**
 * Phase 2.5: Real-time Documentation Completeness Detector
 * 
 * Passively detects documentation indicators from live transcript.
 * NEVER suggests, prompts, or alerts — indicators only.
 * All detection is keyword-based from surgeon-spoken words.
 */

import type { DocumentationCompleteness } from './types';
import { EMPTY_COMPLETENESS } from './types';

/** Primary procedure keywords */
const PRIMARY_PROCEDURE_KEYWORDS = [
  'procedure performed',
  'procedure description',
  'operative technique',
  'surgical technique',
  'total knee',
  'total hip',
  'arthroplasty',
  'arthroscopy',
  'rotator cuff',
  'meniscectomy',
  'carpal tunnel',
  'laminectomy',
  'fusion',
  'osteotomy',
  'fracture fixation',
  'repair',
  'reconstruction',
  'replacement',
  'decompression',
  'excision',
  'incision',
  'resection',
];

/** Ancillary procedure keywords */
const ANCILLARY_KEYWORDS = [
  'debridement',
  'chondroplasty',
  'synovectomy',
  'acromioplasty',
  'subacromial decompression',
  'hardware removal',
  'bone grafting',
  'manipulation',
  'lysis of adhesions',
  'irrigation',
  'biopsy',
  'injection',
  'neurolysis',
];

/** Separate compartment / separate work indicators */
const SEPARATE_WORK_KEYWORDS = [
  'separate compartment',
  'separate incision',
  'separate approach',
  'different compartment',
  'additional procedure',
  'distinct procedure',
  'separate operative field',
  'medial compartment',
  'lateral compartment',
  'patellofemoral',
  'separate work',
];

/** Laterality keywords */
const LATERALITY_KEYWORDS = [
  'right',
  'left',
  'bilateral',
  'right side',
  'left side',
  'right knee',
  'left knee',
  'right shoulder',
  'left shoulder',
  'right hip',
  'left hip',
  'right wrist',
  'left wrist',
  'right hand',
  'left hand',
];

/** Assistant surgeon justification keywords */
const ASSISTANT_SURGEON_KEYWORDS = [
  'assistant surgeon',
  'assistant',
  'first assist',
  'surgical assistant',
  'assisting surgeon',
  'two-surgeon',
  'co-surgeon',
  'retraction',
  'complexity requiring',
];

/**
 * Detect documentation completeness indicators from transcript text.
 * Updates passively — no alerts, no prompts.
 */
export function detectDocumentationCompleteness(transcript: string): DocumentationCompleteness {
  if (!transcript.trim()) return { ...EMPTY_COMPLETENESS };

  const lower = transcript.toLowerCase();
  const detectedKeywords: string[] = [];

  const checkKeywords = (keywords: string[]): boolean => {
    for (const kw of keywords) {
      if (lower.includes(kw.toLowerCase())) {
        detectedKeywords.push(kw);
        return true;
      }
    }
    return false;
  };

  return {
    primaryProcedureDetected: checkKeywords(PRIMARY_PROCEDURE_KEYWORDS),
    ancillaryProceduresDetected: checkKeywords(ANCILLARY_KEYWORDS),
    separateCompartmentIndicator: checkKeywords(SEPARATE_WORK_KEYWORDS),
    lateralityDetected: checkKeywords(LATERALITY_KEYWORDS),
    assistantSurgeonJustification: checkKeywords(ASSISTANT_SURGEON_KEYWORDS),
    detectedKeywords,
  };
}
